import React from 'react';
import './ReviewPage.sass';

import cx from 'classnames';

import {Helmet} from 'react-helmet';
import {FormattedMessage, injectIntl} from 'react-intl';
import {connect} from 'react-redux';

import {getOrder} from '../../ducks/order';
import {createProductReview, fetchOrderReviews, isReviewWindowOpen} from '../../ducks/reviews';
import {setBackground} from '../../ducks/ui';

import ErrorPage from '../errorPage/ErrorPage';
import Loader from '../../components/loader/Loader';
import Tags from '../../components/tags/Tags';
import FieldText from '../../components/fields/fieldText/FieldText';
import PrimaryButton from '../../components/button/PrimaryButton';
import Button from '../../components/button/Button';
import ChannelImage from '../channelUtils/ChannelImage';
import ChannelLink from '../channelUtils/ChannelLink';
import CartProduct from '../../components/cartProduct/CartProduct';
import ProductVariantContainer from '../../components/productVariants/ProductVariantContainer';
import RatingInput from '../../components/reviews/RatingInput';
import ReviewItem from '../../components/reviews/ReviewItem';

@injectIntl
@connect()
export default class ReviewPage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      order: null,      // null = loading, false = errored, object = loaded
      reviews: null,    // null = loading, array = accepted reviews for the order
      name: '',
      drafts: {},       // {productCode: {rating, comment}} — in-progress reviews
      submitted: {},    // {productCode: review} — reviews created this session (still pending moderation)
      flagged: {},      // {productCode: true} — started-but-incomplete on the last submit attempt
      submitError: '',
      submitting: false,
    };
  }

  componentDidMount() {
    const {dispatch, params} = this.props;
    const {orderToken} = params;

    dispatch(setBackground());

    Promise.all([
      dispatch(getOrder(orderToken)).then((response) => response.content),
      dispatch(fetchOrderReviews(orderToken)),
    ]).then(
      ([order, reviews]) => this.setState({order, reviews}),
      () => this.setState({order: false})
    );
  }

  // Reviews live per product code, so order items are grouped by product code:
  // each group lists all of its order-item lines but shares one review.
  getProducts = () => {
    const {order} = this.state;
    const byCode = {};

    return order.items.reduce((groups, item) => {
      const product = item.variant && item.variant.product;
      const code = product && product.code;

      if (!code) {
        return groups;
      }

      const line = {
        name: CartProduct.getItemTitle(item, order.createdFromAdmin),
        options: ProductVariantContainer.describeProductChoice(item),
        image: item.image || (product && product.image) || null,
      };

      if (byCode[code]) {
        byCode[code].items.push(line);
      } else {
        byCode[code] = {code, items: [line]};
        groups.push(byCode[code]);
      }

      return groups;
    }, []);
  };

  // Accepted reviews from the API, merged with the ones submitted this session.
  getSubmittedMap = () => {
    const {reviews, submitted} = this.state;

    const fromApi = (reviews || []).reduce((map, review) => {
      map[review.productCode] = review;
      return map;
    }, {});

    return {...fromApi, ...submitted};
  };

  isComplete = (draft) => !!draft && draft.rating > 0 && !!(draft.comment && draft.comment.trim());

  // Once any review exists for the order, its public name is locked in for the
  // rest of the order (returns '' for an anonymous review; null when unlocked).
  getLockedName = () => {
    const submitted = Object.values(this.getSubmittedMap());
    return submitted.length ? (submitted[0].publicName || '') : null;
  };

  getPublicName = () => {
    const locked = this.getLockedName();
    return locked !== null ? locked : this.state.name.trim();
  };

  setRating = (code, rating) => this.setState((state) => ({
    drafts: {...state.drafts, [code]: {...state.drafts[code], rating}},
    flagged: {...state.flagged, [code]: false},
    submitError: '',
  }));

  setComment = (code, comment) => this.setState((state) => ({
    drafts: {...state.drafts, [code]: {...state.drafts[code], comment}},
    flagged: {...state.flagged, [code]: false},
    submitError: '',
  }));

  onSubmit = () => {
    const {dispatch, params} = this.props;
    const {drafts} = this.state;

    const submittedMap = this.getSubmittedMap();
    const pending = this.getProducts().filter((product) => !submittedMap[product.code]);

    // Only one review is required. Empty/incomplete products are never sent; if
    // nothing is complete yet, the first pending product is flagged as required.
    const toSubmit = pending.filter((product) => this.isComplete(drafts[product.code]));

    if (!toSubmit.length) {
      this.setState({flagged: pending.length ? {[pending[0].code]: true} : {}});
      return;
    }

    const publicName = this.getPublicName();

    this.setState({submitting: true, submitError: '', flagged: {}});

    Promise.all(toSubmit.map((product) => {
      const draft = drafts[product.code];

      return dispatch(createProductReview({
        orderToken: params.orderToken,
        productCode: product.code,
        rating: draft.rating,
        comment: draft.comment.trim(),
        ...(publicName ? {publicName} : {}),
      })).then((review) => ({code: product.code, review}));
    })).then(
      (results) => this.setState((state) => {
        const submitted = {...state.submitted};
        const nextDrafts = {...state.drafts};

        results.forEach(({code, review}) => {
          submitted[code] = review;
          delete nextDrafts[code];
        });

        return {submitted, drafts: nextDrafts, submitting: false};
      }),
      () => this.setState({submitting: false, submitError: intl.formatMessage({id: 'page.review.error.failed'})})
    );
  };

  renderForm = (product) => {
    const {intl} = this.props;
    const {drafts, flagged} = this.state;

    const draft = drafts[product.code] || {};
    const isFlagged = !!flagged[product.code];
    const commentMissing = isFlagged && !(draft.comment && draft.comment.trim());

    return (
      <div className="order-review__form">
        <div className="order-review__label">
          {intl.formatMessage({id: 'page.review.rating-label'})}
        </div>

        <RatingInput
          value={draft.rating || 0}
          onChange={(rating) => this.setRating(product.code, rating)}
        />

        <div className="order-review__form-comment">
          <FieldText
            type="textarea"
            input={{
              name: `comment-${product.code}`,
              value: draft.comment || '',
              onChange: (e) => this.setComment(product.code, e.target.value),
            }}
            meta={{
              touched: commentMissing,
              error: commentMissing ? intl.formatMessage({id: 'page.review.comment-required'}) : '',
            }}
            placeholder={intl.formatMessage({id: 'page.review.comment-placeholder'})}
          />
        </div>
      </div>
    );
  };

  renderSubmitted = (review) => (
    <div className="order-review__review">
      <ReviewItem
        comment={review.comment}
        rating={review.rating}
        createdAt={review.createdAt}
        publicName={review.publicName}
        reply={review.reply}
      />
    </div>
  );

  renderProduct = (product, review, windowOpen) => (
    <div key={product.code} className="order-review__product">
      {product.items.map((item, idx) => (
        <div key={idx} className="order-review__product-head">
          <div className="order-review__product-image">
            {item.image
              ? <ChannelImage src={item.image} alt={item.name}/>
              : <span className="order-review__product-image-placeholder"/>}
          </div>

          <div className="order-review__product-info">
            <div className="order-review__product-name">{item.name}</div>
            {!!(item.options && item.options.length) && (
              <div className="order-review__product-options">
                <Tags tags={item.options}/>
              </div>
            )}
          </div>
        </div>
      ))}

      {review
        ? this.renderSubmitted(review)
        : (windowOpen ? this.renderForm(product) : null)}
    </div>
  );

  render() {
    const {intl} = this.props;
    const {order, reviews, name, submitting, submitError} = this.state;

    if (order === false) {
      return <ErrorPage/>;
    }

    if (!order || reviews === null) {
      return <Loader page/>;
    }

    const products = this.getProducts();
    const submittedMap = this.getSubmittedMap();
    const pending = products.filter((product) => !submittedMap[product.code]).length;
    const anySubmitted = Object.keys(submittedMap).length > 0;

    // Full success = every product reviewed → big banner, no form.
    // Partial success = some reviewed, some pending → small alert + form.
    const fullSuccess = anySubmitted && pending === 0;
    const partialSuccess = anySubmitted && pending > 0;

    // Reviews can only be left within the order's window; once closed, the form
    // is hidden (existing reviews stay visible read-only) and a notice is shown.
    const windowOpen = isReviewWindowOpen(order);
    const showForm = windowOpen && pending > 0;
    const showClosedNotice = !windowOpen && !fullSuccess;

    // Once a review exists, the public name is locked to it (null = still editable).
    const lockedName = this.getLockedName();

    return (
      <React.Fragment>
        <Helmet>
          <title>{intl.formatMessage({id: 'page.review.title'})}</title>
          <meta name="robots" content="noindex, nofollow"/>
        </Helmet>

        <div className="order-review">
          <div className="order-review__inner">
            {fullSuccess ? (
              <div className="order-review__success">
                <span className="order-review__success-icon"/>
                <h1 className="order-review__success-title">
                  {intl.formatMessage({id: 'page.review.success.title'})}
                </h1>
                <p className="order-review__success-text">
                  {intl.formatMessage({id: 'page.review.success.text'})}
                </p>
              </div>
            ) : (
              <h1 className="order-review__heading">
                {intl.formatMessage({id: 'page.review.heading'})}
              </h1>
            )}

            {partialSuccess && windowOpen && (
              <div className="order-review__banner">
                <span className="order-review__banner-icon"/>
                <div className="order-review__banner-text">
                  <b><FormattedMessage id="page.review.banner.thanks"/></b>
                  {' '}
                  <span className="order-review__banner-pending">
                    <FormattedMessage id="page.review.banner.pending" values={{count: pending}}/>
                  </span>
                </div>
              </div>
            )}

            {showClosedNotice && (
              <div className="order-review__notice">
                <FormattedMessage id="page.review.closed"/>
              </div>
            )}

            <div className={cx('order-review__panel', products.length === 1 && 'order-review__panel--single')}>
              <div className="order-review__eyebrow">
                <FormattedMessage id="page.review.order"/>
              </div>
              <div className="order-review__number">#{order.number}</div>
              {showForm && (
                <p className="order-review__intro">
                  <FormattedMessage id="page.review.intro"/>
                </p>
              )}

              {showForm && (
                <div className="order-review__name">
                  <FieldText
                    input={{
                      name: 'publicName',
                      value: lockedName !== null ? lockedName : name,
                      onChange: (e) => {
                        if (lockedName === null) {
                          this.setState({name: e.target.value});
                        }
                      },
                    }}
                    meta={{}}
                    placeholder={intl.formatMessage({id: 'page.review.public-name-placeholder'})}
                    maxLength={100}
                    disabled={lockedName !== null}
                  />
                </div>
              )}

              {products.map((product) => this.renderProduct(product, submittedMap[product.code], windowOpen))}

              {showForm && (
                <div className="order-review__submit">
                  {submitError && <div className="order-review__error">{submitError}</div>}
                  <PrimaryButton big loading={submitting} onClick={this.onSubmit}>
                    <FormattedMessage id={products.length > 1 ? 'page.review.submit-plural' : 'page.review.submit'}/>
                  </PrimaryButton>
                </div>
              )}

            </div>

            {(fullSuccess || showClosedNotice) && (
              <div className="order-review__back">
                <Button component={ChannelLink} to="/" big>
                  {intl.formatMessage({id: 'page.review.back'})}
                </Button>
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./containers/reviewPage/ReviewPage.js